/**
 * Die Buckets eines Szenarios neben dem Pivot: das Change-Set, wer was künftig erbringt. Ein Baumknoten
 * wird auf einen Bucket gezogen und wird dort zu einer Regel (der Filter des Knotens, kein
 * Positionsverzeichnis); die spezifischste Regel gewinnt, gleich spezifische mit verschiedenem Ziel sind
 * ein Konflikt, und was keine Regel trifft, bleibt Rest (tri-server docs/fm-baselining-entwurf.md 7.3).
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { AlertTriangle, Download, FileText, Loader2, Plus, Trash2, X } from "lucide-react";

import { authenticationAtom } from "../../State";
import { useBaseliningAPI, type BucketKind, type BucketLvResult, type Scenario, type ScenarioTotals } from "../../BaseliningAPI";
import { configuration } from "../../Configuration";
import { CONFLICT, NODE_DRAG_TYPE, REST, REST_SWATCH, documentHref, formatEuro, formatShare, swatchFor } from "./pivotModel";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

const KINDS: BucketKind[] = ["supplier", "internal", "outOfScope"];

export default function BucketPanel({
  scenarios,
  scenarioId,
  totals,
  onSelect,
  onChanged,
}: {
  scenarios: Scenario[];
  scenarioId: string | null;
  totals: ScenarioTotals | null;
  onSelect: (id: string | null) => void;
  /** Regeln oder Buckets haben sich geändert — Baum und Summen neu laden. */
  onChanged: (selectScenario?: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useBaseliningAPI(authentication?.customer ?? "");
  const scenario = scenarios.find((s) => s.id === scenarioId) ?? null;

  const [newScenario, setNewScenario] = useState("");
  const [bucketName, setBucketName] = useState("");
  const [bucketKind, setBucketKind] = useState<BucketKind>("supplier");
  const [supplierName, setSupplierName] = useState("");
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // Zweimal klicken statt eines Browser-Dialogs: das erste Klicken scharf schalten, das zweite löscht.
  const [armed, setArmed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Das zuletzt erzeugte LV je Bucket; nur in dieser Sitzung, die Dateien selbst liegen im Speicher.
  const [lvOf, setLvOf] = useState<Record<string, BucketLvResult | "loading">>({});

  const buildLv = async (bucketId: string) => {
    setLvOf((m) => ({ ...m, [bucketId]: "loading" }));
    try {
      const result = await api.bucketLv(bucketId);
      setLvOf((m) => ({ ...m, [bucketId]: result }));
    } catch (e: any) {
      setLvOf(({ [bucketId]: _dropped, ...rest }) => rest);
      toast.error(e?.message ?? t("baselining.analysis.actionFailed"));
    }
  };

  /** Eine Änderung ausführen und neu laden; liefert die Arbeit eine Szenario-Id, wird es gewählt. */
  const act = async (work: () => Promise<unknown>, success?: string, select?: string) => {
    setBusy(true);
    try {
      const result = await work();
      if (success) toast.success(success);
      onChanged(select ?? (typeof result === "string" ? result : undefined));
    } catch (e: any) {
      toast.error(e?.message ?? t("baselining.analysis.actionFailed"));
    } finally {
      setBusy(false);
      setArmed(null);
    }
  };

  const addScenario = () =>
    act(async () => {
      const created = await api.createScenario(newScenario.trim());
      setNewScenario("");
      return created.id;
    });

  const addBucket = () =>
    scenario &&
    act(async () => {
      await api.createBucket(scenario.id, { name: bucketName.trim(), kind: bucketKind, supplierName: supplierName.trim() || null });
      setBucketName("");
      setSupplierName("");
    });

  const drop = (bucket: string, event: React.DragEvent) => {
    event.preventDefault();
    setDropTarget(null);
    if (!scenario) return;
    const raw = event.dataTransfer.getData(NODE_DRAG_TYPE);
    if (!raw) return;
    let node: { filter: Record<string, string>; label: string };
    try {
      node = JSON.parse(raw);
    } catch {
      return;
    }
    const target = scenario.buckets.find((b) => b.id === bucket);
    void act(async () => {
      const result = await api.putRule(scenario.id, { bucket, filter: node.filter, label: node.label });
      toast.success(
        result.moved
          ? t("baselining.buckets.ruleMoved", { node: node.label, bucket: target?.name })
          : t("baselining.buckets.ruleAdded", { node: node.label, bucket: target?.name }),
      );
      return undefined;
    });
  };

  const slots = Object.fromEntries((scenario?.buckets ?? []).map((b) => [b.id, b.slot]));
  const amountOf = (key: string) => totals?.byBucket[key] ?? 0;
  const share = (key: string) => (totals && totals.total ? Math.abs(amountOf(key)) / Math.abs(totals.total) : 0);
  const ruleLabel = (rule: Scenario["rules"][number]) => rule.label || Object.values(rule.filter).join(" › ");

  return (
    <Card>
      <CardHeader className="space-y-2 pb-2">
        <CardTitle className="text-base">{t("baselining.buckets.title")}</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {scenarios.length > 0 && (
            <Select value={scenarioId ?? ""} onValueChange={(v) => onSelect(v || null)}>
              <SelectTrigger className="h-8 w-56">
                <SelectValue placeholder={t("baselining.buckets.chooseScenario")} />
              </SelectTrigger>
              <SelectContent>
                {scenarios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Input
            className="h-8 w-48"
            value={newScenario}
            onChange={(e) => setNewScenario(e.target.value)}
            placeholder={t("baselining.buckets.newScenario")}
            onKeyDown={(e) => e.key === "Enter" && newScenario.trim() && void addScenario()}
          />
          <Button size="sm" variant="outline" disabled={busy || !newScenario.trim()} onClick={() => void addScenario()}>
            <Plus className="mr-1 h-4 w-4" />
            {t("baselining.buckets.addScenario")}
          </Button>
          {scenario && (
            <Button
              size="sm"
              variant={armed === scenario.id ? "destructive" : "ghost"}
              disabled={busy}
              onClick={() => (armed === scenario.id ? void act(() => api.deleteScenario(scenario.id), undefined, "") : setArmed(scenario.id))}
              aria-label={t("baselining.buckets.deleteScenario")}
            >
              <Trash2 className="h-4 w-4" />
              {armed === scenario.id && <span className="ml-1">{t("baselining.buckets.reallyDelete")}</span>}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!scenario ? (
          <p className="text-muted-foreground">{t(scenarios.length ? "baselining.buckets.pickScenario" : "baselining.buckets.noScenario")}</p>
        ) : (
          <>
            {totals && (
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">
                  {t("baselining.buckets.total")}: {formatEuro(totals.total, i18n.language)}
                </Badge>
                <Badge variant="outline">
                  {t("baselining.buckets.addressable")}: {formatEuro(totals.addressable, i18n.language)}
                </Badge>
                <Badge variant={totals.complete ? "secondary" : "outline"}>
                  {totals.complete ? t("baselining.buckets.complete") : t("baselining.buckets.incomplete")}
                </Badge>
              </div>
            )}

            {scenario.buckets.length === 0 && <p className="text-muted-foreground">{t("baselining.buckets.noBuckets")}</p>}

            <div className="space-y-2">
              {scenario.buckets.map((bucket) => {
                const rules = scenario.rules.filter((r) => r.bucket === bucket.id);
                return (
                  <div
                    key={bucket.id}
                    className={`rounded-md border p-2 transition-colors ${dropTarget === bucket.id ? "border-primary bg-primary/5" : ""}`}
                    onDragOver={(e) => {
                      if (!e.dataTransfer.types.includes(NODE_DRAG_TYPE)) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "copy";
                      setDropTarget(bucket.id);
                    }}
                    onDragLeave={() => setDropTarget((d) => (d === bucket.id ? null : d))}
                    onDrop={(e) => drop(bucket.id, e)}
                    aria-label={t("baselining.buckets.dropOn", { bucket: bucket.name })}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`h-3 w-3 shrink-0 rounded-sm ${swatchFor(bucket.id, slots)}`} aria-hidden />
                      <span className="flex-1 truncate font-medium">{bucket.name}</span>
                      <Badge variant="outline" className="h-5 px-1 text-[11px]">
                        {t(`baselining.buckets.kind.${bucket.kind}`)}
                        {bucket.supplierName ? ` · ${bucket.supplierName}` : ""}
                      </Badge>
                      <span className="w-24 shrink-0 text-right font-mono text-xs">{formatEuro(amountOf(bucket.id), i18n.language)}</span>
                      <span className="w-10 shrink-0 text-right text-xs text-muted-foreground">{formatShare(share(bucket.id))}</span>
                      <button
                        type="button"
                        className="rounded p-0.5 text-muted-foreground hover:bg-muted disabled:opacity-40"
                        disabled={busy || rules.length === 0 || lvOf[bucket.id] === "loading"}
                        onClick={() => void buildLv(bucket.id)}
                        aria-label={t("baselining.buckets.buildLv")}
                        title={rules.length === 0 ? t("baselining.buckets.lvNeedsRules") : t("baselining.buckets.buildLv")}
                      >
                        {lvOf[bucket.id] === "loading" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        className={`rounded p-0.5 ${armed === bucket.id ? "text-destructive" : "text-muted-foreground"} hover:bg-muted`}
                        disabled={busy}
                        onClick={() => (armed === bucket.id ? void act(() => api.deleteBucket(bucket.id)) : setArmed(bucket.id))}
                        aria-label={armed === bucket.id ? t("baselining.buckets.reallyDelete") : t("baselining.buckets.deleteBucket")}
                        title={armed === bucket.id ? t("baselining.buckets.reallyDelete") : t("baselining.buckets.deleteBucket")}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {rules.length === 0 ? (
                      <p className="mt-1 text-xs text-muted-foreground">{t("baselining.buckets.dropHint")}</p>
                    ) : (
                      <ul className="mt-1 space-y-0.5">
                        {rules.map((rule) => (
                          <li key={rule.id} className="flex items-center gap-1 text-xs text-muted-foreground">
                            <span className="flex-1 truncate" title={ruleLabel(rule)}>
                              {ruleLabel(rule)}
                            </span>
                            <button
                              type="button"
                              className="rounded p-0.5 hover:bg-muted"
                              disabled={busy}
                              onClick={() => void act(() => api.deleteRule(rule.id))}
                              aria-label={t("baselining.buckets.removeRule")}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {lvOf[bucket.id] === "loading" && <p className="mt-2 text-xs text-muted-foreground">{t("baselining.buckets.lvBuilding")}</p>}
                    {lvOf[bucket.id] && lvOf[bucket.id] !== "loading" && <LvResult result={lvOf[bucket.id] as BucketLvResult} />}
                  </div>
                );
              })}

              {totals && amountOf(CONFLICT) !== 0 && (
                <div className="rounded-md border border-[#d03b3b]/60 p-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-[#d03b3b]" aria-hidden />
                    <span className="flex-1 font-medium">{t("baselining.buckets.conflict")}</span>
                    <span className="w-24 shrink-0 text-right font-mono text-xs">{formatEuro(amountOf(CONFLICT), i18n.language)}</span>
                    <span className="w-10 shrink-0 text-right text-xs text-muted-foreground">{formatShare(share(CONFLICT))}</span>
                    <span className="w-[18px]" />
                  </div>
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {totals.conflicts.map((c) => (
                      <li key={c.rules.join("|")}>
                        {c.rules
                          .map((id) => scenario.rules.find((r) => r.id === id))
                          .filter(Boolean)
                          .map((r) => `${ruleLabel(r!)} → ${scenario.buckets.find((b) => b.id === r!.bucket)?.name ?? "?"}`)
                          .join(" ⟷ ")}
                        {" · "}
                        {formatEuro(c.amount, i18n.language)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {totals && (
                <div className="flex items-center gap-2 rounded-md border border-dashed p-2">
                  <span className={`h-3 w-3 shrink-0 rounded-sm ${REST_SWATCH}`} aria-hidden />
                  <span className="flex-1 text-muted-foreground">{t("baselining.buckets.rest")}</span>
                  <span className="w-24 shrink-0 text-right font-mono text-xs">{formatEuro(amountOf(REST), i18n.language)}</span>
                  <span className="w-10 shrink-0 text-right text-xs text-muted-foreground">{formatShare(share(REST))}</span>
                  <span className="w-[18px]" />
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <Input className="h-8 w-40" value={bucketName} onChange={(e) => setBucketName(e.target.value)} placeholder={t("baselining.buckets.bucketName")} />
              <Select value={bucketKind} onValueChange={(v) => setBucketKind(v as BucketKind)}>
                <SelectTrigger className="h-8 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {t(`baselining.buckets.kind.${k}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bucketKind === "supplier" && (
                <Input className="h-8 w-40" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder={t("baselining.buckets.supplierName")} />
              )}
              <Button size="sm" disabled={busy || !bucketName.trim() || scenario.buckets.length >= 7} onClick={() => void addBucket()}>
                <Plus className="mr-1 h-4 w-4" />
                {t("baselining.buckets.addBucket")}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Das erzeugte LV unter dem Bucket: eine Datei je Los, was hineinkam, und die Hinweise. */
function LvResult({ result }: { result: BucketLvResult }) {
  const { t } = useTranslation();
  return (
    <div className="mt-2 space-y-1 border-t pt-2 text-xs">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{t("baselining.buckets.lvTitle", { version: result.version })}</span>
        <span className="text-muted-foreground">{t("baselining.buckets.lvCounts", { cleaning: result.counts.cleaning, tga: result.counts.tga })}</span>
      </div>
      <ul className="space-y-0.5">
        {result.files.map((f) => {
          const lot = result.lots.find((l) => l.lotNo === f.lotNo);
          return (
            <li key={f.url} className="flex items-start justify-between gap-2">
              <a className="flex min-w-0 items-start gap-1 text-primary hover:underline" href={documentHref(configuration.serverUrl, f.url)} target="_blank" rel="noreferrer">
                <Download className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                <span>{f.title}</span>
              </a>
              {lot && <span className="shrink-0 whitespace-nowrap text-muted-foreground">{t("baselining.analysis.linesCount", { count: lot.positions })}</span>}
            </li>
          );
        })}
      </ul>
      {result.warnings.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-4 text-muted-foreground">
          {result.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
